import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { WhatsAppIcon } from "./icons";
import { BUSINESS } from "@/lib/api";
import { trackConversion } from "@/lib/gtag";

const links = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Why Us", to: "/why-us" },
  { label: "Projects", to: "/projects" },
  { label: "Areas We Serve", to: "/areas" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact Us", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const waText = encodeURIComponent("Hi Sudeeksha, I'd like a free quote for invisible grills.");

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "bg-white/90 backdrop-blur-xl border-b border-steel-200" : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[92px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src="/logo-emblem.png" alt="Sudeeksha Invisible Grills logo" width={503} height={525} fetchPriority="high" decoding="async" className="h-14 md:h-[72px] w-auto object-contain" />
          <span className="flex flex-col leading-none">
            <span className="font-display font-black text-lg md:text-2xl tracking-tighter text-steel-900">SUDEEKSHA</span>
            <span className="font-label text-[10px] md:text-xs tracking-[0.28em] uppercase text-steel-500">Invisible Grills</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-label text-sm tracking-wide transition-colors relative group ${
                  isActive ? "text-steel-900" : "text-steel-600 hover:text-steel-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-1.5 left-0 h-[2px] bg-gold-400 transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 shrink-0">
          <a
            href={`https://wa.me/${BUSINESS.whatsapp}?text=${waText}`}
            target="_blank" rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            onClick={trackConversion}
            aria-label="Chat with us on WhatsApp"
            className="hidden sm:flex items-center gap-2 border border-steel-300 text-steel-900 px-4 py-2.5 text-sm font-semibold hover:border-[#25D366] hover:text-[#128C7E] transition-colors"
          >
            <WhatsAppIcon size={16} className="text-[#25D366]" /> WhatsApp
          </a>
          <a
            href={`tel:+91${BUSINESS.phone}`}
            data-testid="nav-call-btn"
            onClick={trackConversion}
            aria-label={`Call us at ${BUSINESS.phoneDisplay}`}
            className="hidden sm:flex items-center gap-2 bg-steel-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-steel-800 transition-colors"
          >
            <Phone size={15} aria-hidden="true" /> Call Now
          </a>
          <a
            href={`https://wa.me/${BUSINESS.whatsapp}?text=${waText}`}
            target="_blank" rel="noreferrer"
            data-testid="nav-whatsapp-mobile"
            onClick={trackConversion}
            aria-label="Chat with us on WhatsApp"
            className="sm:hidden flex items-center justify-center w-11 h-11 bg-[#25D366] text-white hover:brightness-95 transition-all"
          >
            <WhatsAppIcon size={22} />
          </a>
          <a
            href={`tel:+91${BUSINESS.phone}`}
            data-testid="nav-call-mobile"
            onClick={trackConversion}
            aria-label={`Call us at ${BUSINESS.phoneDisplay}`}
            className="sm:hidden flex items-center justify-center w-11 h-11 bg-steel-900 text-white hover:bg-steel-800 transition-colors"
          >
            <Phone size={18} aria-hidden="true" />
          </a>
          <button
            className="lg:hidden text-steel-900"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-nav-menu"
          >
            {open ? <X size={26} aria-hidden="true" /> : <Menu size={26} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-nav-menu" className="lg:hidden bg-white border-t border-steel-200 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-mobile-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `py-3 border-b border-steel-100 font-label text-base ${isActive ? "text-gold-600 font-bold" : "text-steel-800"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a href={`tel:+91${BUSINESS.phone}`} onClick={trackConversion} className="mt-3 bg-steel-900 text-white px-5 py-3 text-center font-semibold flex items-center justify-center gap-2">
            <Phone size={15} /> Call {BUSINESS.phoneDisplay}
          </a>
        </div>
      )}
    </header>
  );
}
